import React, { useState, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";
import { Input, Form, Button, message, Modal, Collapse } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";
import { serverFetch, strDate2Server } from "@extensions";
import ChildModal from "@components/family/child-modal";
import ProfileFields from "@components/family/profile-fields";
import "./account.css";
import { useGetIsPhone } from "extensions";
import { useCompany } from "@redux";
import { clientProfileFields } from "@extensions/enums";

const errMsg = (e) => e?.userMessage || e?.UserMessage || "Ошибка запроса";

const hasEmailInProfile = (c) =>
  c?.email != null && String(c.email).trim() !== "";

const Account = ({ visible, onSubmit, onCancel, pendingAction, validationResult }) => {
  const company = useCompany();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState({});
  const isPhone = useGetIsPhone();

  const [_verifyEmailError, setVerifyEmailError] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [authGuid, setAuthGuid] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const backgroundStyle = company?.catalogBackgroundImage
    ? { backgroundImage: `url("${company.catalogBackgroundImage}")` }
    : undefined;
  // const [formValid, setFormValid] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [childModalOpen, setChildModalOpen] = useState(false);
  // null - создаётся новый участник группы; заполнен - редактируется существующий
  const [editingChild, setEditingChild] = useState(null);
  // Раскрытая строка группы: на телефоне действия показываем только по тапу
  const [activeFamilyId, setActiveFamilyId] = useState(null);
  // Оригинальный адрес почты из профиля нужен, чтобы решить, показывать ли поле почты и кнопку "Получить код".
  const [hasOriginalEmail, setHasOriginalEmail] = useState(false);
  // Обратный отсчет до возможности повторной отправки кода (секунды).
  const [resendSeconds, setResendSeconds] = useState(0);
  const resendTimerRef = useRef(null);

  const getData = useCallback(() => {
    setLoading(true);
    serverFetch("OnlineClient/info", { method: "GET" })
      .then((data) => {
        const parsed = data.dateBirth ? dayjs(data.dateBirth) : null;
        const dateBirth = parsed && typeof parsed.isValid === "function" && parsed.isValid() ? parsed : null;
        const newClient = {
          ...data,
          dateBirth,
        };
        setClient(newClient);
        form.setFieldsValue({
          ...newClient,
          dateBirth,
        });
        if (hasEmailInProfile(newClient)) {
          setCodeSent(false);
          setAuthGuid(null);
          setVerifyCode("");
          setHasOriginalEmail(true);
        } else {
          setHasOriginalEmail(false);
        }
        setLoading(false);
      })
      .catch(() => {
        message.error("Ошибка получения пользователя");
        setLoading(false);
      });
  }, [form]);

  // Загрузка участников группы представителя
  const fetchFamilyData = useCallback(() => {
    serverFetch("OnlineClient/FamilyList", { method: "GET" })
      .then((data) => {
        setFamilyMembers(data || []);
      })
      .catch(() => {
        message.error("Ошибка получения списка группы");
      });
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    // Подтягиваем группу при появлении данных клиента
    if (Object.keys(client).length > 0) {
      fetchFamilyData();
    }
  }, [client, fetchFamilyData]);

  const onFinish = () => {
    setLoading(true);
    const dataToSend = {
      name: client.name,
      dateBirth: client.dateBirth ? strDate2Server(client.dateBirth) : null,
      sex: client.sex,
      email: client.email || "",
    };
    serverFetch("OnlineClient/info", { method: "PUT", bodyData: dataToSend })
      .then(() => {
        setLoading(false);
        message.success("Профиль сохранен");
        if (onSubmit) {
          onSubmit();
        }
      })
      .catch((e) => {
        setLoading(false);
        message.error(errMsg(e));
      });
  };

  const onAutopayCancel = () => {
    if (window.confirm("Отменить подписку?")) {
      setLoading(true);
      serverFetch("account/AutopayCancel", { method: "POST" })
        .then(getData)
        .catch((e) => {
          setLoading(false);
          message.error(`Ошибка ${e.UserMessage}`);
        });
    }
  };

  const onValuesChange = (value) => {
    setClient({
      ...client,
      ...value,
    });
  };

  const openQrModal = () => {
    if (isPhone && client.card) {
      setIsQrModalOpen(true);
    }
  };

  const onQrKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openQrModal();
    }
  };

  const handleVerifyEmailStart = () => {
    const email = String(form.getFieldValue("email") || "").trim();
    if (!email || !email.includes("@")) {
      const requiredMessage = getFormItemValidate("email")[0]?.message;
      setVerifyEmailError(requiredMessage || "Укажите корректный адрес электронной почты");
      return;
    }
    setVerifyEmailError("");
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailStart", {
      method: "POST",
      bodyData: { email },
    })
      .then((data) => {
        const guid = data?.authCode ?? data?.authGuid;
        if (!guid) {
          message.error("Не получен код авторизации");
          return;
        }
        setAuthGuid(guid);
        setCodeSent(true);
        message.success("Код отправлен");
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  const handleVerifyEmailConfirm = () => {
    const code = String(verifyCode || "").trim();
    if (!code) {
      message.warning("Введите код из письма");
      return;
    }
    if (!authGuid) {
      message.error("Сначала запросите код");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailConfirm", {
      method: "POST",
      bodyData: { authGuid, code },
    })
      .then(() => {
        message.success("Почта подтверждена");
        setHasOriginalEmail(true);
        setCodeSent(false);
        setVerifyCode("");
        setAuthGuid(null);
        // Синхронизируем состояние клиента с подтверждённым email
        const confirmedEmail = String(form.getFieldValue("email") || "").trim();
        setClient(prev => ({ ...prev, email: confirmedEmail }));
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  // Сменить адрес почты: сбросить состояние подтверждения, разблокировать поле
  const handleChangeEmail = () => {
    setCodeSent(false);
    setAuthGuid(null);
    setVerifyCode("");
    setVerifyEmailError("");
    setResendSeconds(0);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    form.setFieldValue("email", "");
    setHasOriginalEmail(false); // Помечаем email как неподтверждённый
    setClient(prev => ({ ...prev, email: "" }));
  };

  // Повторная отправка кода
  const handleResendEmail = () => {
    if (resendSeconds > 0) return;
    const email = String(form.getFieldValue("email") || "").trim();
    if (!email || !email.includes("@")) {
      setVerifyEmailError("Укажите корректный адрес электронной почты");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailStart", {
      method: "POST",
      bodyData: { email },
    })
      .then((data) => {
        const guid = data?.authCode ?? data?.authGuid;
        if (!guid) {
          message.error("Не получен код авторизации");
          return;
        }
        setAuthGuid(guid);
        setCodeSent(true);
        message.success("Код отправлен повторно");
        // Перезапускаем таймер повторной отправки
        setResendSeconds(60);
        if (resendTimerRef.current) {
          clearInterval(resendTimerRef.current);
        }
        resendTimerRef.current = setInterval(() => {
          setResendSeconds((prev) => {
            if (prev <= 1) {
              if (resendTimerRef.current) {
                clearInterval(resendTimerRef.current);
                resendTimerRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  // Запуск обратного отсчета при появлении кнопок подтверждения
  useEffect(() => {
    if (codeSent && resendSeconds === 0) {
      setResendSeconds(60);
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
      resendTimerRef.current = setInterval(() => {
        setResendSeconds((prev) => {
          if (prev <= 1) {
            if (resendTimerRef.current) {
              clearInterval(resendTimerRef.current);
              resendTimerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
        resendTimerRef.current = null;
      }
    };
  }, [codeSent]);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
    };
  }, []);

  // emailActions отображаются справа от поля почты в одной строке
  const emailActions = !codeSent ? (
    <Button
      type="primary"
      loading={verifyLoading}
      onClick={handleVerifyEmailStart}
      style={{ maxWidth: 150 }}
    >
      Получить код
    </Button>
  ) : (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Input
          value={verifyCode}
          onChange={(e) => setVerifyCode(e.target.value)}
          placeholder="Код из письма"
          maxLength={32}
          autoComplete="one-time-code"
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          loading={verifyLoading}
          onClick={handleVerifyEmailConfirm}
          style={{ maxWidth: 150 }}
        >
          Подтвердить
        </Button>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, color: "#626b70" }}>
        <span
          onClick={handleChangeEmail}
          style={{ color: "#626b70", cursor: "pointer", textDecoration: "underline" }}
        >
          Указать другой email
        </span>
        <span
          onClick={handleResendEmail}
          style={{ color: "#626b70", cursor: resendSeconds > 0 ? "not-allowed" : "pointer", textDecoration: resendSeconds > 0 ? "none" : "underline" }}
        >
          Повторить отправку{resendSeconds > 0 ? ` (${resendSeconds} сек.)` : ""}
        </span>
      </div>
    </div>
  );

  // Генерация правил валидации если указаны обязательные поля 
  const getFormItemValidate = (name, rules) => {
    if (!company) return [];
    
    const requiredFields = company.requireFormForWidget ? (company.requiredClientProfileFields || 0) : 0;
    let fieldRules = [...(rules || [])];
    
    switch (name) {
      case 'name':
        if (requiredFields & clientProfileFields.Name) {
          fieldRules = [{ required: true, message: 'Пожалуйста, введите ваше имя' }, ...fieldRules];
        }
        break;
      case 'dateBirth':
        if (requiredFields & clientProfileFields.Birthday) {
          fieldRules = [{ type: 'object', required: true, message: 'Пожалуйста, выберите дату рождения' }, ...fieldRules];
        }
        break;
      case 'sex':
        if (requiredFields & clientProfileFields.Sex) {
          fieldRules = [{ required: true, message: 'Пожалуйста, укажите ваш пол' }, ...fieldRules];
        }
        break;
      case 'email':
        if (requiredFields & clientProfileFields.Email) {
          fieldRules = [{ type: 'email', required: true, message: 'Пожалуйста, введите ваш email' }, ...fieldRules];
        }
        break;
      default:
        break;
    }
    
    return fieldRules;
  };

  // Проверить, заполнены ли обязательные поля
  const isFormValid = () => {
    if (!company) return true;
    
    const requiredFields = company.requireFormForWidget ? (company.requiredClientProfileFields || 0) : 0;
    const fields = form.getFieldsValue();
    
    if (requiredFields & clientProfileFields.Name) {
      const name = fields.name || '';
      const trimmedName = name.trim();
      if (!trimmedName || /^[\d\s\+\-\(\)]+$/.test(trimmedName)) return false;
    }
    if (requiredFields & clientProfileFields.Birthday) {
      if (!fields.dateBirth) return false;
    }
    if (requiredFields & clientProfileFields.Sex) {
      // Пол: 0 - женщина, поэтому проверяем именно null/undefined
      if (fields.sex === null || fields.sex === undefined) return false;
    }
    if (requiredFields & clientProfileFields.Email) {
      const emailValue = String(fields.email || "").trim();
      if (!emailValue) return false;
      // Если email требуется, но не подтверждён - форма невалидна
      if (!hasOriginalEmail) return false;
    } else {
      // Если email опционален, но заполнен и не подтверждён - форма невалидна
      const emailValue = String(fields.email || "").trim();
      if (emailValue && !hasOriginalEmail) return false;
    }
    
    return true;
  };

  const qrModal = isPhone && client.card ? (
    <Modal
      open={isQrModalOpen}
      onCancel={() => setIsQrModalOpen(false)}
      footer={null}
      centered
      width="calc(100vw - 24px)"
      wrapClassName="account-qr-modal"
      destroyOnClose
    >
      <div className="account-qr-modal-content">
        <QRCodeSVG value={client.card} size={320} level="H" />
      </div>
    </Modal>
  ) : null;

  // Обработчики управления группой.
  // Участник группы создаётся только новый или редактируется из числа привязанных
  const openChildModal = (child = null) => {
    setEditingChild(child);
    setChildModalOpen(true);
  };
  const closeChildModal = () => {
    setChildModalOpen(false);
    setEditingChild(null);
  };
  const onChildSaved = () => {
    closeChildModal();
    // Перезагружаем список группы
    fetchFamilyData();
  };
  // Кнопка отмены автопродления (само поле рисует ProfileFields);
  const divCard = client.autopayCode ? (
    <Form.Item label=" "><Button onClick={onAutopayCancel}>Отменить подписку</Button></Form.Item>
  ) : null;

  // QR для анкеты: на телефоне выводится под полями и открывает модалку увеличения
  const qrField = !visible && client.card ? (
    <div className="account-qr-code account-qr-code_clickable" role="button" tabIndex={0} onClick={openQrModal} onKeyDown={onQrKeyDown}>
      <QRCodeSVG value={client.card} size={150} level="H" />
    </div>
  ) : null;

  // Поля анкеты - общие со страницей редактирования участника группы, чтобы вид и порядок полей совпадали.
  const divPhone = (
    <ProfileFields
      company={company}
      phone={client.phone}
      autopayCard={client.autopayCode}
      nameDisabled={loading}
      qr={isPhone ? qrField : null}
      emailEditable={!codeSent && !hasOriginalEmail}
      showEmailInput={!codeSent}
      emailActions={!hasOriginalEmail ? emailActions : null}
    />
  );


  // Блок "Группа"
  const familyBlock = (
    <Form.Item label=" " className="account-family-form-item">
      <Collapse ghost defaultActiveKey={["family"]}>
        <Collapse.Panel header={`Группа (${familyMembers.length})`} key="family">
          <div className="account-family-body">
            {familyMembers.length > 0 ? (
              familyMembers.map(member => (
                <div
                  key={member.id}
                  className={`account-family-item ${activeFamilyId === member.id ? "account-family-item_active" : ""}`}
                  onClick={() => setActiveFamilyId(activeFamilyId === member.id ? null : member.id)}
                >
                  <div className="account-family-avatar">
                    {member?.imageId ? (
                      <img src={`${process.env.REACT_APP_API_BASE_URL}/api/image/${member.imageId}.jpg`} alt={member.name} />
                    ) : (
                      member.name ? member.name[0] : '?'
                    )}
                  </div>
                  <div className="account-family-info">
                    <div className="account-family-name" title={member.name}>{member.name}</div>
                    <div className="account-family-meta">
                      {member.dateBirth && <span>{dayjs(member.dateBirth).format("DD.MM.YYYY")}</span>}
                      {member.phone && <span>{member.phone}</span>}
                    </div>
                  </div>
                  <div className="account-family-actions" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      className="account-family-action account-family-edit"
                      onClick={() => openChildModal(member)}
                    >
                      {isPhone ? <EditOutlined /> : "Изменить"}
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="account-family-empty">Список пуст</p>
            )}
          </div>
          <div className="account-family-add">
            <Button type="dashed" onClick={() => openChildModal(null)}>
              + Добавить участника группы
            </Button>
          </div>
        </Collapse.Panel>
      </Collapse>
    </Form.Item>
  );

  // Модалка участника группы - общая с корзиной, чтобы проверка обязательных полей была одна
  const childModal = (
    <ChildModal
      open={childModalOpen}
      child={editingChild}
      onClose={closeChildModal}
      onSaved={onChildSaved}
    />
  );

  const pendingActionMessage = pendingAction ? (
    <div className="account-pending-action">
      <span className="account-pending-action-icon">⚠️</span>
      <span className="account-pending-action-text">Для продолжения данного действия, пожалуйста, заполните данные анкеты</span>
    </div>
  ) : null;
  const formContent = (
    <div className="account-form account-form-root">
      {pendingActionMessage}
      {visible && onCancel && (
        <div className="account-modal-close" onClick={onCancel}>
          ✕
        </div>
      )}
      <Form
        colon={false}
        form={form}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17 }}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
      >
        <div className="auth-title">Мои данные</div>
        <div className="account-content-wrapper">
          <div className="account-form-inner">
            {divPhone}
            {divCard}
            {/* Семейный блок не показываем в модальном режиме и детям */}
            {!visible && !client.representativeId && familyBlock}
            <Form.Item label={isPhone ? "" : " "}>
              <div className="account-modal-buttons">
                <Button className="btn-blue-raund" size="middle" loading={loading} htmlType="submit" disabled={!isFormValid() || codeSent}>
                  {visible ? 'ОК' : 'Сохранить'}
                </Button>
                {visible && onCancel && (
                  <Button onClick={onCancel} className="account-modal-cancel">Отмена</Button>
                )}
              </div>
            </Form.Item>
            <label className="prompt">
              Нажимая на кнопку &quot;{visible ? 'ОК' : 'Сохранить'}&quot;, вы даете согласие на обработку{" "}
              <a href="/article/agreement" target="_blank" rel="noreferrer">
                персональных данных
              </a>{" "}
              и соглашаетесь с{" "}
              <a href="/article/rules" target="_blank" rel="noreferrer">
                Условиями и правилами
              </a>{" "}
              сервиса
            </label>
          </div>
          {!visible && client.card && !isPhone && (
            <div className="account-qr-code">
              <QRCodeSVG value={client.card} size={200} level="H" />
            </div>
          )}
        </div>
      </Form>
      {qrModal}
    </div>
  );

  return (<div className={`auth-context ${visible ? "account-modal" : ""}`} style={visible ? undefined : backgroundStyle}>{formContent}{childModal}</div>);
};

export default Account;
